
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useTypedSelector, prefetchCallLLMActionSource } from "@circuitry-ai/doc-data";
import { VoiceFormAssistant } from "@circuitry-ai/doc-ui";

/**
 * Replica-style FTIR entry form inspired by the provided legacy desktop screen.
 * Tailwind-only version (no shadcn/ui).
 * - Dense, bordered sections
 * - Yellow input fields
 * - Blue dividers/frames
 * - Orange section tags
 */

// -------------------------
// Tailwind-only primitives
// -------------------------
type ButtonVariant = "default" | "primary" | "success" | "danger";
type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
};

type CardProps = React.HTMLAttributes<HTMLDivElement>;
type CardContentProps = React.HTMLAttributes<HTMLDivElement>;
type LblProps = React.LabelHTMLAttributes<HTMLLabelElement>;
type CheckboxProps = React.InputHTMLAttributes<HTMLInputElement> & {
  onCheckedChange?: (checked: boolean) => void;
};
type YInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  fullWidth?: boolean;
};
type YTextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
type SelectItem = { value: string; label: string };
type YSelectProps = {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  items?: SelectItem[];
  className?: string;
};

type SectionTagProps = { children: React.ReactNode };
type FrameProps = { title: string; children: React.ReactNode; right?: React.ReactNode | null };
type FieldProps = { label: string; children: React.ReactNode; className?: string };
type RowProps = { children: React.ReactNode };

type FormState = {
  jobCardNo: string;
  dealerCode: string;
  dealerName: string;
  registrationNo: string;
  modelName: string;
  vin: string;
  chasisNo: string;
  mileage: string;
  engineNo: string;
  saleDate: string;
  dateOfIncident: string;
  actualProblem: string;
  customerReported: string;
  defectLocation: string;
  defectConditions: string;
  checkedBeforeConclusion: string;
  dealerConclusion: string;
  diagnosticCode: string;
  possibleCausalPartNo: string;
  howRepaired: string;
};

type VoiceContext = {
  conditions?: string[];
  speedRelated?: "yes" | "no";
  rpmRelated?: "yes" | "no";
  speedFrom?: string;
  speedTo?: string;
  rpmFrom?: string;
  rpmTo?: string;
  speedComment?: string;
  rpmComment?: string;
  roadConditions?: string[];
};

const defaultFormState: FormState = {
  jobCardNo: "",
  dealerCode: "",
  dealerName: "",
  registrationNo: "",
  modelName: "",
  vin: "",
  chasisNo: "",
  mileage: "",
  engineNo: "",
  saleDate: "",
  dateOfIncident: "",
  actualProblem: "",
  customerReported: "",
  defectLocation: "",
  defectConditions: "",
  checkedBeforeConclusion: "",
  dealerConclusion: "",
  diagnosticCode: "",
  possibleCausalPartNo: "",
  howRepaired: "",
};

const Btn = ({ variant = "default", className = "", ...props }: BtnProps) => {
  const base =
    "inline-flex items-center justify-center rounded-sm border px-3 text-[12px] h-7 select-none";
  const variants = {
    default:
      "border-slate-400 bg-slate-100 text-slate-900 hover:bg-slate-200 active:bg-slate-300",
    primary:
      "border-blue-900 bg-blue-700 text-white hover:bg-blue-800 active:bg-blue-900",
    success:
      "border-green-900 bg-green-600 text-white hover:bg-green-700 active:bg-green-800",
    danger:
      "border-red-900 bg-red-600 text-white hover:bg-red-700 active:bg-red-800",
  };
  return (
    <button className={`${base} ${variants[variant] || variants.default} ${className}`} {...props} />
  );
};

const Card = ({ className = "", ...props }: CardProps) => (
  <div className={"rounded-none border-0 shadow-none " + className} {...props} />
);

const CardContent = ({ className = "", ...props }: CardContentProps) => (
  <div className={"p-0 " + className} {...props} />
);

const Lbl = ({ className = "", ...props }: LblProps) => (
  <label className={"text-[12px] text-slate-800 " + className} {...props} />
);

const Checkbox = ({
  id,
  checked,
  defaultChecked,
  onCheckedChange,
  className = "",
  ...props
}: CheckboxProps) => {
  // If `checked` is provided, behave as a controlled checkbox.
  // Otherwise, behave as an uncontrolled checkbox so it remains clickable without wiring state.
  const baseProps = {
    id,
    type: "checkbox",
    className: "h-4 w-4 accent-blue-700 " + className,
    ...props,
  };

  if (typeof checked === "boolean") {
    return (
      <input
        {...baseProps}
        checked={checked}
        onChange={(e) => onCheckedChange?.(e.target.checked)}
      />
    );
  }

  return (
    <input
      {...baseProps}
      defaultChecked={!!defaultChecked}
      onChange={(e) => onCheckedChange?.(e.target.checked)}
    />
  );
};

const YInput = ({ className = "", fullWidth = true, ...props }: YInputProps) => (
  <input
    className={
      "h-7 rounded-sm border border-slate-400 bg-yellow-200/80 px-2 text-[12px] text-slate-900 outline-none focus:border-slate-600 " +
      (fullWidth ? "w-full " : "") +
      className
    }
    {...props}
  />
);

const YTextarea = ({ className = "", ...props }: YTextareaProps) => (
  <textarea
    className={
      "min-h-[66px] w-full rounded-sm border border-slate-400 bg-yellow-200/80 p-2 text-[12px] leading-snug text-slate-900 outline-none focus:border-slate-600 " +
      className
    }
    {...props}
  />
);

const YSelect = ({ value, onValueChange, placeholder, items = [], className = "" }: YSelectProps) => (
  <select
    className={
      "h-7 w-full rounded-sm border border-slate-400 bg-yellow-200/80 px-2 text-[12px] text-slate-900 outline-none focus:border-slate-600 " +
      className
    }
    value={value}
    onChange={(e) => onValueChange?.(e.target.value)}
  >
    {placeholder ? (
      <option value="" disabled>
        {placeholder}
      </option>
    ) : null}
    {items.map((it) => (
      <option key={it.value} value={it.value}>
        {it.label}
      </option>
    ))}
  </select>
);

// -------------------------
// Layout helpers
// -------------------------
const SectionTag = ({ children }: SectionTagProps) => (
  <div className="inline-flex select-none items-center rounded-none border border-orange-700 bg-orange-400 px-2 py-[2px] text-[11px] font-bold uppercase tracking-wide text-slate-900">
    {children}
  </div>
);

const Frame = ({ title, children, right = null }: FrameProps) => (
  <div className="relative rounded-none border-2 border-blue-700 bg-slate-100">
    <div className="absolute -top-[10px] left-2">
      <SectionTag>{title}</SectionTag>
    </div>

    {right ? (
      <div className="flex justify-end border-b-2 border-blue-700 bg-slate-100 px-2 py-1 pt-3">
        {right}
      </div>
    ) : (
      <div className="border-b-2 border-blue-700 bg-slate-100 px-2 py-1 pt-3" />
    )}

    <div className="p-2">{children}</div>
  </div>
);

const Field = ({ label, children, className = "" }: FieldProps) => (
  <div className={"grid grid-cols-[140px_1fr] items-center gap-2 " + className}>
    <Lbl className="text-[12px] text-slate-800">{label}</Lbl>
    {children}
  </div>
);

const Row = ({ children }: RowProps) => (
  <div className="grid grid-cols-1 gap-2 md:grid-cols-12">{children}</div>
);

export default function FTIRScreenReplica() {
  const dispatch = useAppDispatch();
  const authToken = useTypedSelector((state) => state.auth.token);
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"open" | "in_progress" | "closed">("open");

  const prefill = useMemo(() => {
    const state = location.state as {
      prefill?: Partial<FormState>;
      complaint?: string;
      diagnosticCode?: string;
      possibleCausalPartNo?: string;
    } | null;
    return state?.prefill ?? null;
  }, [location.state]);

  const complaintNote = useMemo(() => {
    const state = location.state as { complaint?: string } | null;
    return state?.complaint ?? "";
  }, [location.state]);

  const voiceContext = useMemo(() => {
    const state = location.state as { voiceContext?: VoiceContext } | null;
    return state?.voiceContext ?? null;
  }, [location.state]);

  const repairDiagnosisState = useMemo(() => {
    const state = location.state as {
      diagnosticCode?: string;
      possibleCausalPartNo?: string;
      repaired?: "yes" | "no" | null;
      solvedAfterRepair?: "yes" | "no" | null;
    } | null;
    return state ?? null;
  }, [location.state]);

  const cameFromJobCard = useMemo(() => {
    const state = location.state as { fromJobCard?: boolean } | null;
    return Boolean(state?.fromJobCard);
  }, [location.state]);

  const [form, setForm] = useState<FormState>(() => ({
    ...defaultFormState,
    ...(prefill ?? {}),
    ...(repairDiagnosisState?.diagnosticCode
      ? { diagnosticCode: repairDiagnosisState.diagnosticCode }
      : {}),
    ...(repairDiagnosisState?.possibleCausalPartNo
      ? { possibleCausalPartNo: repairDiagnosisState.possibleCausalPartNo }
      : {}),
  }));

  const [confirmSelf, setConfirmSelf] = useState<"yes" | "no" | "na">("yes");
  const [repaired, setRepaired] = useState<"yes" | "no">(
    repairDiagnosisState?.repaired ?? "no"
  );
  const [solvedAfterRepair, setSolvedAfterRepair] = useState<"yes" | "no" | "unknown">(
    repairDiagnosisState?.solvedAfterRepair ?? "unknown"
  );
  const [speedRelated, setSpeedRelated] = useState<"yes" | "no">(
    voiceContext?.speedRelated ?? "no"
  );
  const [rpmRelated, setRpmRelated] = useState<"yes" | "no">(
    voiceContext?.rpmRelated ?? "no"
  );
  const [defectivePartAvail, setDefectivePartAvail] = useState<"yes" | "no">("yes");
  const [sentIncidentPart, setSentIncidentPart] = useState<"yes" | "no">("no");
  const [isVoiceCaptureEnabled, setIsVoiceCaptureEnabled] = useState(false);
  const [speedFrom, setSpeedFrom] = useState(voiceContext?.speedFrom ?? "");
  const [speedTo, setSpeedTo] = useState(voiceContext?.speedTo ?? "");
  const [speedComment, setSpeedComment] = useState(voiceContext?.speedComment ?? "");
  const [rpmFrom, setRpmFrom] = useState(voiceContext?.rpmFrom ?? "");
  const [rpmTo, setRpmTo] = useState(voiceContext?.rpmTo ?? "");
  const [rpmComment, setRpmComment] = useState(voiceContext?.rpmComment ?? "");

  const conditions = useMemo<string[]>(
    () => [
      "At Engine Starting",
      "After Engine Start",
      "Starting Off",
      "Accelerating",
      "Cruising",
      "Decelerating",
      "Braking",
      "Before Stop",
      "Idling",
      "Right Turning",
      "Left Turning",
      "Up Hill",
      "Down Hill",
      "Reverse",
      "Heavy Traffic",
      "Shifting Gear",
      "Over Bump",
    ],
    []
  );

  const roadConditionOptions = useMemo<string[]>(
    () => [
      "City Street",
      "Local Road",
      "Highway / Freeway",
      "Mountain",
      "Slope",
      "Sand",
      "High Land",
      "Race Track",
      "Bump",
      "Road Hole",
      "Hump",
      "Rut / Groove",
      "Asphalt",
      "Concrete",
      "Soling",
      "Flat dirt",
      "Gravel",
      "Sand",
      "Mud",
      "Fresh Snow",
      "Compacted Snow",
      "Slush",
      "Ice",
      "Water Depth",
    ],
    []
  );

  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    const initial = Object.fromEntries(conditions.map((c) => [c, false])) as Record<
      string,
      boolean
    >;
    if (voiceContext?.conditions?.length) {
      voiceContext.conditions.forEach((label) => {
        if (label in initial) {
          initial[label] = true;
        }
      });
    }
    return initial;
  });

  const toggle = (k: string) => setChecked((p) => ({ ...p, [k]: !p[k] }));

  const [roadChecked, setRoadChecked] = useState<Record<string, boolean>>(() => {
    const initial = Object.fromEntries(
      roadConditionOptions.map((c) => [c, false])
    ) as Record<string, boolean>;
    if (voiceContext?.roadConditions?.length) {
      voiceContext.roadConditions.forEach((label) => {
        if (label in initial) {
          initial[label] = true;
        }
      });
    }
    return initial;
  });

  const toggleRoad = (k: string) => setRoadChecked((p) => ({ ...p, [k]: !p[k] }));

  useEffect(() => {
    // Warm the action's source_id cache up front so voice capture doesn't pay for the
    // `GET /api/v1/sources?source_type=action` lookup on its first LLM call. Skip until we
    // have an auth token so this doesn't fire (and 401) before login/session restore completes.
    if (!authToken) return;
    prefetchCallLLMActionSource(dispatch);
  }, [dispatch, authToken]);

  // Dev-only sanity checks
  if (import.meta.env.DEV) {
    console.assert(conditions.length === 17, "Expected 17 condition checkboxes");
    console.assert(
      Object.keys(checked).length === conditions.length,
      "Checkbox state keys should match condition labels"
    );
  }

  return (
    <div className="min-h-screen bg-slate-300 p-2 md:p-4">
      <div className="mx-auto max-w-[1220px] h-full overflow-y-auto max-h-screen">
        {/* Title Bar */}
        <div className="rounded-none border-2 border-blue-800 bg-blue-700 px-3 py-2 text-white">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[13px] font-semibold">
              Field Technical Information Report New (1.1.12)
            </div>
            <div className="flex items-center gap-2">
              <div className="text-[11px] opacity-90">Status</div>
              <div className="flex items-center gap-2">
                <div className="min-w-[170px]">
                  <YSelect
                    value={status}
                    onValueChange={setStatus}
                    items={[
                      { value: "open", label: "Open" },
                      { value: "in_progress", label: "In Progress" },
                      { value: "closed", label: "Closed" },
                    ]}
                  />
                </div>
                <div className="h-6 w-28 rounded-sm border border-slate-200 bg-green-500/70" />
              </div>
              <div className="ml-2">
                <VoiceFormAssistant
                  enabled={isVoiceCaptureEnabled}
                  dispatch={dispatch}
                  onToggle={setIsVoiceCaptureEnabled}
                  narrationMode={true}
                  statusText={{
                    idle: "Voice capture idle",
                    listening: "Voice capture active",
                    processing: "Listening...",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {complaintNote ? (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-none border border-blue-700 bg-blue-50 px-3 py-2 text-[12px] text-slate-800">
            <div>
              <span className="font-semibold">Prefilled from Job Card complaint:</span>{" "}
              {complaintNote}
            </div>
            {cameFromJobCard ? (
              <Btn type="button" variant="primary" onClick={() => navigate("/job-card")}>
                Back to Job Card
              </Btn>
            ) : null}
          </div>
        ) : null}

        <div className="mt-2">
          <Card>
            <CardContent className="space-y-2">
              {/* FTIR DETAIL */}
              <Frame
                title="FTIR DETAIL"
                right={
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 text-[11px] text-slate-900">
                      <Checkbox id="copy" />
                      <Lbl htmlFor="copy" className="text-[11px]">
                        Copy As Existing FTIR
                      </Lbl>
                    </div>
                    <div className="flex items-center gap-2">
                      <Lbl className="text-[11px]">FTIR No.</Lbl>
                      <YInput className="w-40" />
                    </div>
                    <div className="hidden max-w-[300px] text-[10px] text-red-700 lg:block">
                      Disclaimer: The information and photograph(s) uploaded on DMS may be used for
                      internal purposes.
                    </div>
                    <Btn variant="danger" type="button">
                      <span className="mr-2 inline-flex h-4 w-4 items-center justify-center" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4">
                          <path
                            d="M12 16V4m0 0l-4 4m4-4l4 4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      Image Upload
                    </Btn>
                  </div>
                }
              >
                <Row>
                  <div className="col-span-12 space-y-2 md:col-span-6">
                    <Field label="Dealer Code">
                      <YInput
                        value={form.dealerCode}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, dealerCode: e.target.value }))
                        }
                      />
                    </Field>
                    <Field label="For Code">
                      <YInput />
                    </Field>
                    <Field label="Individual Reply">
                      <div className="flex gap-2">
                        <YInput />
                        <Btn type="button">...</Btn>
                      </div>
                    </Field>
                  </div>

                  <div className="col-span-12 space-y-2 md:col-span-6">
                    <Field label="Dealer Name">
                      <YInput
                        value={form.dealerName}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, dealerName: e.target.value }))
                        }
                      />
                    </Field>
                    <Field label="Outlet Code">
                      <div className="grid grid-cols-[1fr_120px] gap-2">
                        <YInput />
                        <div className="grid grid-cols-[60px_1fr] items-center gap-2">
                          <Lbl className="text-[11px] text-slate-800">(Region)</Lbl>
                          <YInput />
                        </div>
                      </div>
                    </Field>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-12 lg:col-span-6">
                        <Field label="Global FTIR No.">
                          <YInput />
                        </Field>
                      </div>
                      <div className="col-span-12 lg:col-span-6">
                        <Field label="Job Card No.">
                          <YInput
                            value={form.jobCardNo}
                            onChange={(e) =>
                              setForm((p) => ({ ...p, jobCardNo: e.target.value }))
                            }
                          />
                        </Field>
                      </div>
                    </div>
                  </div>
                </Row>
              </Frame>

              {/* VEHICLE DETAIL */}
              <Frame title="VEHICLE DETAIL">
                <Row>
                  <div className="col-span-12 space-y-2 md:col-span-6">
                    <Field label="Registration No.">
                      <YInput
                        value={form.registrationNo}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, registrationNo: e.target.value }))
                        }
                      />
                    </Field>
                    <Field label="VIN">
                      <YInput
                        value={form.vin}
                        onChange={(e) => setForm((p) => ({ ...p, vin: e.target.value }))}
                      />
                    </Field>
                  </div>

                  <div className="col-span-12 space-y-2 md:col-span-6">
                    <Field label="Model Name">
                      <div className="grid grid-cols-[1fr_26px] gap-2">
                        <YInput
                          value={form.modelName}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, modelName: e.target.value }))
                          }
                        />
                        <Btn type="button" className="w-[26px] px-0">
                          v
                        </Btn>
                      </div>
                    </Field>
                    <Field label="Chassis No.">
                      <YInput
                        value={form.chasisNo}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, chasisNo: e.target.value }))
                        }
                      />
                    </Field>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-12 lg:col-span-6">
                        <Field label="Mileage">
                          <YInput
                            value={form.mileage}
                            onChange={(e) =>
                              setForm((p) => ({ ...p, mileage: e.target.value }))
                            }
                          />
                        </Field>
                      </div>
                      <div className="col-span-12 space-y-2 lg:col-span-6">
                        <Field label="Engine No.">
                          <YInput
                            value={form.engineNo}
                            onChange={(e) =>
                              setForm((p) => ({ ...p, engineNo: e.target.value }))
                            }
                          />
                        </Field>
                        <Field label="Sale Date">
                          <YInput
                            value={form.saleDate}
                            onChange={(e) =>
                              setForm((p) => ({ ...p, saleDate: e.target.value }))
                            }
                          />
                        </Field>
                      </div>
                    </div>
                  </div>
                </Row>
              </Frame>

              {/* DETAIL */}
              <Frame title="DETAIL">
                <Row>
                  <div className="col-span-12 space-y-2 md:col-span-7">
                    <Field label="What is the actual problem along with condition & Defective part?">
                      <div className="flex gap-2">
                        <YInput
                          value={form.actualProblem}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, actualProblem: e.target.value }))
                          }
                        />
                        <Btn type="button">...</Btn>
                      </div>
                    </Field>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-12 lg:col-span-6">
                        <Field label="Created By">
                          <YInput />
                        </Field>
                      </div>
                      <div className="col-span-12 lg:col-span-6">
                        <Field label="Date of Incident">
                          <YInput
                            value={form.dateOfIncident}
                            onChange={(e) =>
                              setForm((p) => ({ ...p, dateOfIncident: e.target.value }))
                            }
                          />
                        </Field>
                      </div>
                    </div>
                    <Field label="Approved By">
                      <YInput />
                    </Field>
                    <Field label="Service Dealer Name">
                      <YInput />
                    </Field>
                  </div>
                </Row>
              </Frame>

              {/* DESCRIPTION */}
              <Frame title="DESCRIPTION">
                <Row>
                  <div className="col-span-12 space-y-2 md:col-span-7">
                    <Field label="What exactly is reported by the customer?">
                      <div className="flex gap-2">
                        <YInput
                          value={form.customerReported}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, customerReported: e.target.value }))
                          }
                        />
                        <Btn type="button">...</Btn>
                      </div>
                    </Field>

                    <Field label="Could you confirm the problem yourself?">
                      <YSelect
                        value={confirmSelf}
                        onValueChange={setConfirmSelf}
                        items={[
                          { value: "yes", label: "Yes" },
                          { value: "no", label: "No" },
                          { value: "na", label: "N/A" },
                        ]}
                      />
                    </Field>

                    <Field label="Defect Occuring Location in Vehicle?">
                      <YInput
                        value={form.defectLocation}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, defectLocation: e.target.value }))
                        }
                      />
                    </Field>

                    <Field label="Defect Occuring Conditions (Parked / Running)?">
                      <YInput
                        value={form.defectConditions}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, defectConditions: e.target.value }))
                        }
                      />
                    </Field>

                    <Field label="What all was checked by you before arriving on the conclusion?">
                      <YInput
                        value={form.checkedBeforeConclusion}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            checkedBeforeConclusion: e.target.value,
                          }))
                        }
                      />
                    </Field>

                    <Field label="What is your (Dealer) conclusion after analysis?">
                      <YInput
                        value={form.dealerConclusion}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, dealerConclusion: e.target.value }))
                        }
                      />
                    </Field>
                  </div>

                  <div className="col-span-12 space-y-2 md:col-span-5">
                    <div className="space-y-2 rounded-sm border-2 border-blue-700 bg-slate-50 p-2">
                      <div className="text-[11px] font-semibold text-slate-800">
                        Repair / Diagnosis
                      </div>

                      <Field label="Diagnostic Code" className="grid-cols-[160px_1fr]">
                        <YInput
                          value={form.diagnosticCode}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, diagnosticCode: e.target.value }))
                          }
                        />
                      </Field>

                      <Field label="Possible Causal Part No." className="grid-cols-[160px_1fr]">
                        <YInput
                          value={form.possibleCausalPartNo}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              possibleCausalPartNo: e.target.value,
                            }))
                          }
                        />
                      </Field>

                      <Field label="Whether vehicle repaired or not?" className="grid-cols-[160px_1fr]">
                        <YSelect
                          value={repaired}
                          onValueChange={setRepaired}
                          items={[
                            { value: "yes", label: "Yes" },
                            { value: "no", label: "No" },
                          ]}
                        />
                      </Field>

                      <Field
                        label="Whether vehicle problem solved or not after repair?"
                        className="grid-cols-[160px_1fr]"
                      >
                        <YSelect
                          value={solvedAfterRepair}
                          onValueChange={setSolvedAfterRepair}
                          items={[
                            { value: "yes", label: "Yes" },
                            { value: "no", label: "No" },
                            { value: "unknown", label: "Unknown" },
                          ]}
                        />
                      </Field>

                      <Field
                        label="How vehicle was repaired? If not, Why not repaired"
                        className="grid-cols-[160px_1fr] items-start"
                      >
                        <YTextarea
                          className="min-h-[56px]"
                          value={form.howRepaired}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, howRepaired: e.target.value }))
                          }
                        />
                      </Field>
                    </div>
                  </div>
                </Row>
              </Frame>

              {/* INCIDENT (DEFECTIVE) PART */}
              <Frame title="INCIDENT (DEFECTIVE) PART">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-12 md:col-span-4">
                    <Field label="Is Defective Part available?">
                      <YSelect
                        value={defectivePartAvail}
                        onValueChange={setDefectivePartAvail}
                        items={[
                          { value: "yes", label: "Yes" },
                          { value: "no", label: "No" },
                        ]}
                      />
                    </Field>
                  </div>

                  <div className="col-span-12 md:col-span-4">
                    <Field label="Have you sent Incident Part?">
                      <YSelect
                        value={sentIncidentPart}
                        onValueChange={setSentIncidentPart}
                        items={[
                          { value: "yes", label: "Yes" },
                          { value: "no", label: "No" },
                        ]}
                      />
                    </Field>
                  </div>

                  <div className="col-span-12 md:col-span-4">
                    <Field label="Part Dispatch Date">
                      <YInput />
                    </Field>
                  </div>

                  <div className="col-span-12 md:col-span-8">
                    <Field label="If not, Why?">
                      <YInput />
                    </Field>
                  </div>
                </div>
              </Frame>

              {/* CONDITIONS */}
              <Frame title="CONDITIONS OF PROBLEM OCCURING AS PER CUSTOMER VOICE?">
                <div className="space-y-3">
                  <div className="rounded-sm border border-slate-400 bg-slate-50 p-2">
                    <div className="grid grid-cols-[70px_1fr] gap-2">
                      <div className="text-[11px] font-semibold text-slate-800">When?</div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[11px] md:grid-cols-4 lg:grid-cols-6">
                        {conditions.map((c) => (
                          <label key={c} className="flex items-center gap-2 text-slate-800">
                            <Checkbox checked={checked[c]} onCheckedChange={() => toggle(c)} />
                            <span>{c}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-12 md:col-span-6">
                      <div className="rounded-sm border border-slate-400 bg-slate-50 p-2">
                        <div className="grid grid-cols-[70px_1fr] items-start gap-2">
                          <div className="text-[11px] font-semibold text-slate-800">Speed</div>
                          <div className="space-y-2">
                            <Field label="Speed Related?" className="grid-cols-[120px_1fr]">
                              <YSelect
                                value={speedRelated}
                                onValueChange={setSpeedRelated}
                                items={[
                                  { value: "yes", label: "Yes" },
                                  { value: "no", label: "No" },
                                ]}
                              />
                            </Field>
                            <div className="grid grid-cols-[120px_1fr_26px_1fr_52px] items-center gap-2">
                              <Lbl className="text-[11px]">Driving Speed</Lbl>
                              <YInput value={speedFrom} onChange={(e) => setSpeedFrom(e.target.value)} />
                              <div className="text-center text-[11px]">To</div>
                              <YInput value={speedTo} onChange={(e) => setSpeedTo(e.target.value)} />
                              <div className="text-[11px]">Km/hr</div>
                            </div>
                            <Field label="Comment" className="grid-cols-[120px_1fr]">
                              <YInput
                                value={speedComment}
                                onChange={(e) => setSpeedComment(e.target.value)}
                              />
                            </Field>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <div className="rounded-sm border border-slate-400 bg-slate-50 p-2">
                        <div className="grid grid-cols-[70px_1fr] items-start gap-2">
                          <div className="text-[11px] font-semibold text-slate-800">Engine RPM</div>
                          <div className="space-y-2">
                            <Field label="RPM Related" className="grid-cols-[120px_1fr]">
                              <YSelect
                                value={rpmRelated}
                                onValueChange={setRpmRelated}
                                items={[
                                  { value: "yes", label: "Yes" },
                                  { value: "no", label: "No" },
                                ]}
                              />
                            </Field>
                            <div className="grid grid-cols-[120px_1fr_26px_1fr] items-center gap-2">
                              <Lbl className="text-[11px]">Occuring RPM</Lbl>
                              <YInput value={rpmFrom} onChange={(e) => setRpmFrom(e.target.value)} />
                              <div className="text-center text-[11px]">To</div>
                              <YInput value={rpmTo} onChange={(e) => setRpmTo(e.target.value)} />
                            </div>
                            <Field label="Comment" className="grid-cols-[120px_1fr]">
                              <YInput
                                value={rpmComment}
                                onChange={(e) => setRpmComment(e.target.value)}
                              />
                            </Field>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-sm border border-slate-400 bg-slate-50 p-2">
                    <div className="grid grid-cols-[110px_1fr] items-start gap-2">
                      <div className="text-[11px] font-semibold text-slate-800">Road Condition</div>

                      <div className="space-y-2 text-[11px]">
                        <div className="flex flex-wrap gap-x-4 gap-y-2">
                          {[
                            "City Street",
                            "Local Road",
                            "Highway / Freeway",
                            "Mountain",
                            "Slope",
                            "Sand",
                            "High Land",
                            "Race Track",
                          ].map((r) => (
                            <label key={r} className="flex items-center gap-2 text-slate-800">
                              <Checkbox checked={roadChecked[r]} onCheckedChange={() => toggleRoad(r)} />
                              <span>{r}</span>
                            </label>
                          ))}
                        </div>

                        <div className="flex flex-wrap gap-x-6 gap-y-2">
                          {["Bump", "Road Hole", "Hump", "Rut / Groove"].map((r) => (
                            <label key={r} className="flex items-center gap-2 text-slate-800">
                              <Checkbox checked={roadChecked[r]} onCheckedChange={() => toggleRoad(r)} />
                              <span>{r}</span>
                            </label>
                          ))}
                        </div>

                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            <div className="min-w-[92px] text-slate-800">Paved Road</div>
                            <div className="text-slate-800">[</div>
                            {["Asphalt", "Concrete", "Soling"].map((r) => (
                              <label key={r} className="flex items-center gap-2 text-slate-800">
                                <Checkbox checked={roadChecked[r]} onCheckedChange={() => toggleRoad(r)} />
                                <span>{r}</span>
                              </label>
                            ))}
                            <div className="text-slate-800">]</div>
                          </div>

                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            <div className="min-w-[92px] text-slate-800">Unpaved Road</div>
                            <div className="text-slate-800">[</div>
                            {["Flat dirt", "Gravel", "Sand", "Mud"].map((r) => (
                              <label key={r} className="flex items-center gap-2 text-slate-800">
                                <Checkbox checked={roadChecked[r]} onCheckedChange={() => toggleRoad(r)} />
                                <span>{r}</span>
                              </label>
                            ))}
                            <div className="text-slate-800">]</div>
                          </div>

                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            <div className="min-w-[92px] text-slate-800">Snowy Road</div>
                            <div className="text-slate-800">[</div>
                            {["Fresh Snow", "Compacted Snow", "Slush", "Ice"].map((r) => (
                              <label key={r} className="flex items-center gap-2 text-slate-800">
                                <Checkbox checked={roadChecked[r]} onCheckedChange={() => toggleRoad(r)} />
                                <span>{r}</span>
                              </label>
                            ))}
                            <div className="text-slate-800">]</div>
                          </div>

                            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            <div className="min-w-[92px] text-slate-800">Flooded</div>
                            <div className="text-slate-800">[</div>
                            <label className="flex items-center gap-2 text-slate-800">
                              <Checkbox
                                checked={roadChecked["Water Depth"]}
                                onCheckedChange={() => toggleRoad("Water Depth")}
                              />
                              <span>Water Depth</span>
                            </label>
                            <YInput className="h-6 w-10" fullWidth={false} placeholder="" />
                            <div className="whitespace-nowrap text-[11px] text-slate-700">eg. 20 cm</div>
                            <div className="text-slate-800">]</div>
                          </div>
                        </div>

                        <div className="grid grid-cols-[70px_1fr] items-center gap-2">
                          <Lbl className="text-[11px] text-slate-800">Comment</Lbl>
                          <YInput />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Frame>

              {/* CUSTOMER USAGE */}
              <Frame title="CUSTOMER USAGE">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-12 space-y-2 md:col-span-6">
                    <Field label="Application">
                      <YInput />
                    </Field>
                    <Field label="Comment">
                      <YInput />
                    </Field>
                    <div className="rounded-sm border border-slate-400 bg-slate-50 p-2">
                      <div className="text-[11px] font-semibold text-slate-800">Type Of Driving</div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] md:grid-cols-3">
                        {[
                          "City Street",
                          "Sloping",
                          "Local Road",
                          "Highway / Freeway",
                          "Mountain",
                          "Snow Region",
                          "Coast",
                        ].map((t) => (
                          <label key={t} className="flex items-center gap-2 text-slate-800">
                            <Checkbox />
                            <span>{t}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="col-span-12 space-y-2 md:col-span-6">
                    <Field label="Average Distance Running per Day" className="grid-cols-[220px_1fr]">
                      <div className="flex items-center gap-2">
                        <YInput className="max-w-[220px]" />
                        <div className="text-[11px] text-slate-800">In Km.</div>
                      </div>
                    </Field>

                    <div className="rounded-sm border border-slate-400 bg-slate-50 p-2">
                      <div className="text-[11px] font-semibold text-slate-800">Parking Situation</div>
                      <div className="mt-2 space-y-2">
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-12 lg:col-span-7">
                            <Field label="Parking Location" className="grid-cols-[120px_1fr]">
                              <YSelect
                                value={"open"}
                                onValueChange={() => {}}
                                items={[
                                  { value: "open", label: "Open" },
                                  { value: "covered", label: "Covered" },
                                  { value: "basement", label: "Basement" },
                                ]}
                              />
                            </Field>
                          </div>
                          <div className="col-span-12 lg:col-span-5">
                            <Field label="Car Covered" className="grid-cols-[90px_1fr]">
                              <YSelect
                                value={"yes"}
                                onValueChange={() => {}}
                                items={[
                                  { value: "yes", label: "Yes" },
                                  { value: "no", label: "No" },
                                ]}
                              />
                            </Field>
                          </div>
                        </div>

                        <Field
                          label="Surface Condition of Parking Location"
                          className="grid-cols-[220px_1fr]"
                        >
                          <YInput />
                        </Field>
                        <Field label="Comment" className="grid-cols-[70px_1fr]">
                          <YInput />
                        </Field>
                      </div>
                    </div>
                  </div>
                </div>
              </Frame>

              {/* Footer controls */}
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-none border-2 border-blue-800 bg-slate-200 px-2 py-2">
                <div className="text-[11px] text-slate-700">
                  * Please ensure all relevant details are accurately captured before saving or submitting this report.
                </div>
                <div className="flex flex-wrap gap-2">
                  <Btn type="button">Draft</Btn>
                  <Btn type="button" variant="success">
                    Save
                  </Btn>
                  <Btn type="button" variant="primary">
                    Submit
                  </Btn>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
